// src/utils/errors.ts

/**
 * Consistent, frontend-safe error message extractor.
 * Looks at common backend shapes: { detail: { message } } | { detail } | { message }.
 * Extended to understand Firebase Auth codes and prefer their friendly equivalents.
 */

function firebaseCodeToMessage(code?: string): string | undefined {
  // Map of Firebase Auth error codes → friendly messages.
  switch (code) {
    case "auth/invalid-email":
      return "Invalid email address.";
    case "auth/user-disabled":
      return "This account has been disabled.";
    case "auth/user-not-found":
      return "We couldn't find an account with that email.";
    case "auth/wrong-password":
      return "Incorrect password. Please try again.";
    case "auth/invalid-credential":
      return "Invalid email or password.";
    case "auth/too-many-requests":
      return "Too many attempts. Please try again later.";
    case "auth/email-already-in-use":
      return "An account already exists for that email.";
    case "auth/weak-password":
      return "Password is too weak.";
    case "auth/operation-not-allowed":
      return "This sign-in method is not allowed.";
    default:
      return undefined;
  }
}

export function getErrorMessage(err: any, fallback = "Something went wrong"): string {
  if (!err) return fallback;

  // Firebase Auth error codes
  const fromCode = firebaseCodeToMessage(typeof err?.code === "string" ? err.code : undefined);
  if (fromCode) return fromCode;

  // Common backend shapes
  const fromDetailMessage = err?.detail?.message;
  if (typeof fromDetailMessage === "string" && fromDetailMessage.trim()) return fromDetailMessage.trim();

  const fromDetail = err?.detail;
  if (typeof fromDetail === "string" && fromDetail.trim()) return fromDetail.trim();

  const fromMessage = err?.message;
  if (typeof fromMessage === "string" && fromMessage.trim()) return fromMessage.trim();

  return fallback;
}

/**
 * Returns true if the error looks like an optimistic locking conflict (HTTP 409).
 */
export function isVersionConflict(err: any): boolean {
  return Number(err?.status) === 409;
}

/**
 * Extract current server version from conflict payload if present.
 */
export function conflictCurrentVersion(err: any): number | undefined {
  const v = err?.detail?.current_version;
  return Number.isFinite(v) ? Number(v) : undefined;
}
